import React, { useEffect, useState, useCallback, useMemo } from 'react';
import cn from 'classnames';
/* Hooks & context */
import useToggle from '@hooks/useToggle';

/* App Store: mobile */

/* Preval Data */
import headerData from '@integrations/preval/headerdata.preval';

/*  Types */
import { User } from 'types/user.types';

/* Components */
import { PrimaryMenu } from './Parts';

/* Styles */
import styles from './nav.module.scss';
import { IServicesNavColumn } from 'src/constants/nav';
import LayeredMenu from './Parts/LayeredMenu';
import { TLayer } from './Parts/LayeredMenu/layeredMenu.types';

type NavProps = {
  isOpen: boolean;
  user?: User;
  skuLinkPrefix?: string;
  hideMembership?: boolean;
  isMobile?: boolean;
  logout?: BaseAnyFunction;
};

const Nav = ({ isOpen, user, skuLinkPrefix, hideMembership, logout, isMobile }: NavProps) => {
  const [layers, setLayers] = useState<TLayer[]>([]);
  const [layerLevel, setLayerLevel] = useState(-1);
  /*  Nav Controller */
  const { toggleState: navIsOpen, setBooleanToggle: setNavIsOpenState } = useToggle(false);

  // Use internal state to show nav
  useEffect(() => {
    if (isOpen) {
      setNavIsOpenState(true);
    } else {
      const timeoutId = window.setTimeout(() => {
        // Reset menus on close
        setLayers([]);
        setLayerLevel(-1);
        setNavIsOpenState(false);
      }, 0);
      return () => window.clearTimeout(timeoutId);
    }
    return () => {};
  }, [isOpen, setNavIsOpenState]);

  // Styles
  const navContainerStyles = cn(styles.navContainer, {
    [styles.open]: navIsOpen,
  });

  const { servicesMenu = [] }: { servicesMenu: IServicesNavColumn[] } = headerData;

  const servicesCategories = useMemo(() => servicesMenu.map(e => e.categories || []).flat(), [servicesMenu]);

  const setServiceMenuVisibleState = useCallback(() => {
    setLayers([
      {
        name: 'Services',
        categories: servicesCategories,
      },
    ]);
    setLayerLevel(0);
  }, [servicesCategories]);

  return (
    <nav className={navContainerStyles}>
      <PrimaryMenu
        user={user}
        setServiceMenuVisibleState={setServiceMenuVisibleState}
        hideMembership={hideMembership}
        logout={logout}
        isMobile={isMobile}
      />
      {layers.map((layer, index) => (
        <LayeredMenu
          key={index}
          layer={layer}
          visible={index <= layerLevel}
          onBackClick={() => setLayerLevel(index - 1)}
          skuLinkPrefix={skuLinkPrefix}
          onSelectChild={child => {
            setLayers([...layers.slice(0, index + 1), child]);
            setTimeout(() => setLayerLevel(index + 1), 0);
          }}
        />
      ))}
    </nav>
  );
};

export default React.memo(Nav);
